/**
 * "Open this file for good" — the double-click half of VS Code's two-gesture rule.
 *
 * A single click previews: one italic tab that the next click replaces, so
 * clicking through a tree never buries the user in tabs. A double-click promotes
 * that tab, and from then on it survives previews. This module is the decision;
 * the caller supplies the app-specific preview open (which in the editor also
 * kicks off the content fetch, loading state and bundle-sibling registry).
 *
 * It lives apart from `EditorLayout` so the browser harness can drive the REAL
 * rule against the REAL tree instead of a copy of it — a copy is exactly how a
 * wiring bug survives its own test.
 */

import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import type { FileNode } from '@/components/molecules/metadata_v1';

export interface OpenFilePersistentlyOptions {
  /**
   * Open `file` as a preview tab, as a single click would. Called only when the
   * file has no tab yet — a double-click that follows a click (the normal case)
   * finds the preview already open and just promotes it, which is what keeps
   * unsaved edits and in-flight content untouched.
   */
  openPreview: (file: FileNode) => void;
}

export function openFilePersistently(
  file: FileNode,
  { openPreview }: OpenFilePersistentlyOptions,
): void {
  if (file.type !== 'file') return;

  const store = useMetadataEditorStore.getState();
  const existing = store.getTabByFileId(file.id);

  if (!existing) {
    // Reached without a preceding select — keyboard, quick-open, a programmatic
    // reveal. Open, then promote, so "open persistently" is never a no-op.
    openPreview(file);
    const opened = useMetadataEditorStore.getState().getTabByFileId(file.id);
    if (opened) useMetadataEditorStore.getState().promotePreviewTab(opened.id);
    return;
  }

  store.setActiveTab(existing.id);
  if (existing.isPreview) store.promotePreviewTab(existing.id);
}
